import { ISeriesApi, LineData, SeriesType, Time } from 'lightweight-charts'
import Chart from './chart'
import { CrossHairInfoMultiStrikeChart } from './chart.typings'
import { ChartCandle } from '../chartV2/chart.typings'
import { formatLargeNumber } from '../../util/screener'
import { MultiStrikeChartTypes } from '../../reducer/multiStrikeChart.reducer'
import { isOiValueChartType } from './multistrikeChartTypeHelpers'
import LineSeries from './series/lineSeries'
import { calculateOptionOutlook } from '../../util/options'
import { Outlook, outlookToJSON } from '../../proto/generated/candle-data-depot-option-chain'
import { OutlookToName } from '../../pages/typings/options.typings'
import { green, orange, red } from '../../assets/colors'

function getLineSeries(chart: Chart | undefined, seriesKey: string | undefined): LineSeries | undefined {
    if (!chart || !seriesKey) return undefined
    if (seriesKey.startsWith('cum_oi_')) {
        return chart.dataManager.cumSeriesApis[seriesKey]
    }
    return chart.dataManager.seriesApis[seriesKey]
}

/** Last index in sorted candle data where time <= target. */
function findNearestIndex(arr: LineData<Time>[], target: number): number {
    let low = 0
    let high = arr.length - 1
    let result = -1
    while (low <= high) {
        const mid = Math.floor((low + high) / 2)
        const time = arr[mid]?.time
        if (time === undefined) {
            high = mid - 1
            continue
        }
        const timeValue = typeof time === 'number' ? time : Number(time)
        if (timeValue <= target) {
            result = mid
            low = mid + 1
        } else {
            high = mid - 1
        }
    }
    return result
}

function findNearestRawCandleIndex(rawData: ChartCandle[], target: number): number {
    return findNearestIndex(
        rawData.map((c) => ({ time: c.time, value: c.close ?? 0 })),
        target
    )
}

function getPrimaryLtpChange(chart: Chart | undefined, time: number): number {
    const primaryData = chart?.dataManager.data[chart.primary]
    if (!primaryData?.length) return 0
    const index = findNearestRawCandleIndex(primaryData, time)
    if (index <= 0) return 0
    const candle = primaryData[index]
    const prev = primaryData[index - 1]
    return (candle?.close ?? 0) - (prev?.close ?? 0)
}

export function getOutlookDisplayColor(tag: string): string {
    if ([OutlookToName.LONG_BUILDUP, OutlookToName.SHORT_COVERING].includes(tag)) return green
    if (tag === OutlookToName.LONG_UNWINDING) return orange
    if (tag === OutlookToName.SHORT_BUILDUP) return red
    return ''
}

export function resolveSeriesOutlookDisplay(
    info: CrossHairInfoMultiStrikeChart | undefined,
    chart: Chart | undefined,
    seriesKey: string | undefined
): { tag: string; color: string } {
    const empty = { tag: '-', color: '' }
    const series = getLineSeries(chart, seriesKey)
    const rawData = series?.rawData as ChartCandle[] | undefined
    if (!rawData?.length) return empty

    const hoverTime = info?.point && info.time ? info.time : undefined
    const index =
        hoverTime !== undefined ? findNearestRawCandleIndex(rawData, hoverTime) : rawData.length - 1
    if (index <= 0) return empty

    const candle = rawData[index]
    const prev = rawData[index - 1]
    if (!candle || !prev) return empty

    const isCumSeries = Boolean(seriesKey?.startsWith('cum_oi_'))
    const oiChange = isCumSeries
        ? (candle.series ?? (candle.openInterest ?? 0) - (prev.openInterest ?? 0))
        : (candle.openInterest ?? 0) - (prev.openInterest ?? 0)
    const ltpChange = isCumSeries
        ? getPrimaryLtpChange(chart, typeof candle.time === 'number' ? candle.time : Number(candle.time))
        : (candle.close ?? 0) - (prev.close ?? 0)

    const outlook = calculateOptionOutlook(ltpChange, oiChange)
    if ([Outlook.OUTLOOK_UNKNOWN, Outlook.UNRECOGNIZED].includes(outlook)) {
        return empty
    }

    const tag = OutlookToName[outlookToJSON(outlook)]
    return { tag, color: getOutlookDisplayColor(tag) }
}

export function resolveSeriesDisplayValue(
    info: CrossHairInfoMultiStrikeChart | undefined,
    chart: Chart | undefined,
    seriesApi: ISeriesApi<SeriesType, Time> | undefined,
    seriesKey: string | undefined
): number | undefined {
    if (seriesApi && info?.seriesData?.has(seriesApi)) {
        return info.seriesData.get(seriesApi)?.value
    }
    const series = getLineSeries(chart, seriesKey)
    const data: LineData<Time>[] | undefined = series?.data
    if (!data?.length) return undefined
    const hoverTime = info?.time
    if (hoverTime) {
        let index = findNearestIndex(data, hoverTime)
        while (index >= 0) {
            const value = data[index]?.value
            if (value !== undefined) return value
            index -= 1
        }
    }
    return data[data.length - 1]?.value
}

export function formatMultiStrikeSeriesValue(value: number | undefined, chartType: MultiStrikeChartTypes): string {
    if (value === undefined) return '-'
    if (isOiValueChartType(chartType)) {
        return formatLargeNumber(value, 2)
    }
    return value.toFixed(2)
}
